import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios, { AxiosError } from 'axios';
import * as RouterPath from '../data/RouterPath';
import { headers as baseHeaders } from '../data/Config';
import { useLanguageStore } from '../store/useLanguageStore';

interface PatProps {
  created_at: number;
  id: string;
  name: string;
  revoked: boolean;
  hidden: boolean;
  scopes: string[];
}

interface PatsResponse {
  result: {
    pats: PatProps[];
  };
}

/**
 * 사용 위치: Tokens.tsx - 토큰 목록 화면에서 토큰 데이터 로딩/삭제/상태관리를 담당하는 커스텀 훅
 *
 * 설명:
 * - 토큰 목록을 조회하고(pats) 삭제(revokeToken)할 수 있습니다.
 * - tokenState는 외부에서 갱신 트리거로 사용할 수 있습니다. (예: 'none' -> 'changed')
 * - isRevoked 값이 변경되거나 tokenState가 바뀌면 목록을 재조회합니다.
 */

const useTokens = (
  baseUrl: string,
  isRevoked: boolean,
  accessToken: string
) => {
  const { languagePack } = useLanguageStore();
  const navigate = useNavigate();
  const [pats, setPats] = useState<PatProps[]>([]);
  const [tokenState, setTokenState] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 요청 전용 헤더(전역 headers를 변경하지 않도록 매 요청마다 병합)
  const requestHeaders = useMemo(
    () => ({
      ...baseHeaders,
      Authorization: `Bearer ${accessToken}`,
    }),
    [accessToken]
  );

  /**
   * 사용 위치: useEffect 내부에서 호출 - 컴포넌트 마운트/갱신 시 토큰 목록 조회
   */
  const fetchPats = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get<PatsResponse>(`${baseUrl}/pats`, {
          headers: requestHeaders,
          signal,
        });

        setPats(response.data.result.pats);
      } catch (err) {
        const error = err as AxiosError<any>;
        if (axios.isAxiosError(error)) {
          const status = error.response?.status;
          if (status === 401) {
            // 401 처리
            if (
              error.response?.data?.error === 'EMP Not Agreed Service Term' &&
              window.location.pathname !== RouterPath.LOGIN
            ) {
              window.alert(languagePack?.tokens.empAgreed ?? 'Unauthorized');
            }
            navigate(RouterPath.LOGIN);
          } else if (status) {
            console.error('token list error', error);
            setError(`Request failed with status ${status}`);
          } else if (error.message === 'canceled') {
            // 요청 취소
            // no-op
          } else {
            console.error('token list unknown error', error);
            setError('Unknown error occurred');
          }
        } else {
          console.error('non-axios error', err);
          setError('Unknown error occurred');
        }
      } finally {
        setLoading(false);
      }
    },
    [baseUrl, navigate, requestHeaders, languagePack?.tokens.empAgreed]
  );
  /**
   * 사용 위치: Tokens.tsx - 토큰 삭제(폐기) 버튼 클릭 시 호출
   */
  const revokeToken = useCallback(
    async (
      token: PatProps,
      setIsRevoked: (revoked: boolean) => void, // 사용 위치: Tokens.tsx - 삭제 후 UI 상태 갱신
      setRevokedToken: (name: string) => void, // 사용 위치: Tokens.tsx - 알림/스낵바에 토큰명 표시
      handleClose: () => void // 사용 위치: Tokens.tsx - 확인 다이얼로그 닫기
    ) => {
      try {
        const res = await axios.delete(`${baseUrl}/pat/${token.id}`, {
          headers: requestHeaders,
        });

        if (res.status === 200) {
          // 해당 토큰 제거 및 상태 갱신
          setPats((prevPats) => prevPats.filter((t) => t.id !== token.id));
          setTokenState('none');
          setIsRevoked(true);
          setRevokedToken(token.name);
          handleClose();
        }
      } catch (err) {
        console.error('Revoke token error', err);
      }
    },
    [baseUrl, requestHeaders]
  );

  useEffect(() => {
    // 사용 위치: 마운트, isRevoked 변경, tokenState 변경, baseUrl 변경 시 목록 재조회
    const controller = new AbortController();
    fetchPats(controller.signal);
    return () => controller.abort();
  }, [baseUrl, isRevoked, tokenState, fetchPats]);

  return {
    // 사용 위치: Tokens.tsx - 목록 렌더링
    pats,
    // 사용 위치: Tokens.tsx - 외부에서 상태 플래그로 활용(리스트 재조회 트리거)
    tokenState,
    setTokenState,
    // 사용 위치: Tokens.tsx - 토큰 개별 삭제
    revokeToken,
    // 사용 위치: Tokens.tsx - 로딩/에러 UI 제어(선택적 사용)
    loading,
    error,
  };
};

export default useTokens;
